import { ChevronRight } from "lucide-react";
import { useNavigate } from "react-router";
import { BlogPostThumbnail } from "./BlogPostThumbnail";
import { StatusPill, type BlogStatus } from "./StatusPill";
import { TagChip } from "./TagChip";
import { formatLongDate, relativeFromNow, relativeUntil } from "../date-utils";

const MAX_VISIBLE_TAGS = 3;

export interface BlogPostRowData {
  id: string;
  title: string;
  author: string;
  status: BlogStatus;
  publishAt: string | null;
  updatedAt: string;
  body: string | null;
  tagNames: string[];
  featuredImageUrl: string | null;
}

/**
 * One post row in the blog listing. Layout matches the mock —
 * thumbnail / metadata block / right-aligned date column — and the
 * whole row is the click target for editing.
 */
export function BlogPostRow({ post }: { post: BlogPostRowData }) {
  const navigate = useNavigate();
  const readingTime = computeReadingTime(post.body);
  const tags = post.tagNames ?? [];
  const visibleTags = tags.slice(0, MAX_VISIBLE_TAGS);
  const overflowTags = tags.length - visibleTags.length;
  const subtitle = formatSubtitle(post.author, readingTime, post.status);
  const longDate = formatLongDate(post.publishAt ?? post.updatedAt);
  const relative = formatRelative(post);

  return (
    <button
      type="button"
      onClick={() => navigate(`/website/blog/${post.id}`)}
      className="group relative flex w-full cursor-pointer items-center gap-4 px-5 py-4 text-left transition-colors duration-150 hover:bg-paper-2 active:bg-canvas/60 focus-visible:bg-paper-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-tide/40"
    >
      {/* Hover accent — a thin tide bar that slides in along the left
          edge to signal the row is the click target. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-2 left-0 w-[3px] origin-top -translate-x-1 scale-y-50 rounded-r bg-tide opacity-0 transition-all duration-150 group-hover:translate-x-0 group-hover:scale-y-100 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:scale-y-100 group-focus-visible:opacity-100"
      />

      <BlogPostThumbnail
        status={post.status}
        publishAt={post.publishAt}
        featuredImageUrl={post.featuredImageUrl}
        seed={post.id}
      />
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center gap-2 overflow-hidden">
          <StatusPill status={post.status} />
          {tags.length > 0 && (
            <>
              <span className="text-[11px] text-ink-5">·</span>
              <div className="flex min-w-0 items-center gap-1">
                {visibleTags.map((tag) => (
                  <TagChip key={tag} label={tag} />
                ))}
                {overflowTags > 0 && (
                  <TagChip label={`+${overflowTags}`} muted />
                )}
              </div>
            </>
          )}
        </div>
        <div className="font-display truncate text-[17px] font-medium text-ink transition-colors duration-150 group-hover:text-tide group-focus-visible:text-tide">
          {post.title || "Untitled"}
        </div>
        <div className="mt-0.5 truncate text-[12px] text-ink-4">{subtitle}</div>
      </div>
      <div className="flex-shrink-0 text-right text-[11px] text-ink-4">
        <div className="num">{longDate}</div>
        <div className="mt-0.5">{relative}</div>
      </div>
      <ChevronRight
        size={16}
        strokeWidth={1.8}
        aria-hidden
        className="flex-shrink-0 -translate-x-1 text-ink-5 opacity-0 transition-all duration-150 group-hover:translate-x-0 group-hover:text-tide group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100"
      />
    </button>
  );
}

function formatSubtitle(
  author: string,
  readingTime: number,
  status: BlogStatus,
): string {
  const parts: string[] = [];
  if (author) parts.push(author);
  if (readingTime > 0) parts.push(`${readingTime} min read`);
  if (status === "draft") parts.push("not yet published");
  return parts.join(" · ");
}

function computeReadingTime(html: string | null): number {
  if (!html) return 0;
  const text = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  if (!text) return 0;
  const words = text.split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 220));
}

function formatRelative(post: BlogPostRowData): string {
  if (post.status === "draft") {
    const ago = relativeFromNow(post.updatedAt);
    return ago ? `Last edit: ${ago}` : "Last edit: just now";
  }
  if (post.status === "scheduled" && post.publishAt) {
    const future = relativeUntil(post.publishAt);
    return future ? `In ${future}` : "Scheduled";
  }
  return relativeFromNow(post.publishAt ?? post.updatedAt) ?? "just now";
}
